import { useNavigate, useLocation } from 'react-router-dom';
import LanguageSwitcher from "./LanguageSwitcher";
import { User, LogIn, UserPlus, Plus, Shield, LogOut, Search, Share2 } from 'lucide-react';
import ReferralNotificationsBell from './ReferralNotificationsBell';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from "@/contexts/LanguageContext";
import Avatar from './Avatar';
import { useState, useRef, useEffect } from 'react';
import FullImageModal from './FullImageModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PageHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasRole, logout, getAuthHeader } = useAuth();
  const [showFullImage, setShowFullImage] = useState(false);
  const { t } = useLanguage();
  const [showDropdown, setShowDropdown] = useState(false);
  const [userPoints, setUserPoints] = useState(0);
  const dropdownRef = useRef(null);
  
  // Game progress state (for game pages)
  const [gameProgress, setGameProgress] = useState(null);

  // Fetch user points
  useEffect(() => {
    const fetchUserPoints = async () => {
      if (!user) {
        setUserPoints(0);
        return;
      }
      try {
        const resp = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
        setUserPoints(resp.data.total_points || 0);
      } catch (error) {
        console.error('Error fetching user points:', error);
      }
    };
    fetchUserPoints();
  }, [user]);

  // Ref to track current points for animation
  const pointsRef = useRef(userPoints);
  const animationIdRef = useRef(null);
  
  useEffect(() => { pointsRef.current = userPoints; }, [userPoints]);

  // Register global function for points update with animation
  useEffect(() => {
    window.updatePointsWithAnimation = (newBalance) => {
      console.log("[PageHeader] updatePointsWithAnimation called:", newBalance, "current:", pointsRef.current);
      
      const targetValue = newBalance;
      const startValue = pointsRef.current;
      const diff = targetValue - startValue;
      
      if (diff === 0) return;
      
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      
      const duration = 2000;
      const startTime = performance.now();
      
      const step = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - (1 - progress) * (1 - progress);
        const currentValue = Math.round(startValue + diff * eased);
        
        setUserPoints(currentValue);
        pointsRef.current = currentValue;
        
        if (progress < 1) {
          animationIdRef.current = requestAnimationFrame(step);
        } else {
          console.log("[PageHeader] Animation complete:", targetValue);
        }
      };
      
      animationIdRef.current = requestAnimationFrame(step);
    };
    
    console.log("[PageHeader] Registered window.updatePointsWithAnimation");
    
    return () => {
      delete window.updatePointsWithAnimation;
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
    };
  }, []);


  // Fetch game progress for game pages and view pages
  useEffect(() => {
    const fetchGameProgress = async () => {
      // Check if we're on a game page or view page
      const gameMatch = location.pathname.match(/^\/([a-f0-9-]{36})$/);
      const viewMatch = location.pathname.match(/^\/view\/([^\/]+)$/);
      
      if (!user) {
        setGameProgress(null);
        return;
      }
      
      try {
        if (viewMatch) {
          // View page - show hotspots ONLY for the current panorama
          const panoramaId = viewMatch[1];

          const [hotspotsResp, foundResp] = await Promise.all([
            axios.get(`${API}/panoramas/${panoramaId}/hotspots`).catch(() => ({ data: [] })),
            axios.get(`${API}/panoramas/${panoramaId}/hotspots/found?_t=${Date.now()}`, { headers: getAuthHeader() })
              .catch(() => ({ data: { found_hotspot_ids: [] } })),
          ]);

          const panoramaHotspots = hotspotsResp.data || [];
          const foundHotspotIds = foundResp.data?.found_hotspot_ids || [];

          setGameProgress({
            panoramasTotal: 0, // Don't show panoramas progress on view page
            panoramasAnswered: 0,
            hotspotsTotal: panoramaHotspots.length,
            hotspotsFound: foundHotspotIds.length
          });
        } else if (gameMatch) {
          // Game page - aggregate hotspots across all panoramas of this game
          const gameId = gameMatch[1];

          // Fetch panoramas
          const panoramasResp = await axios.get(`${API}/games/${gameId}/panoramas`);
          const panoramas = panoramasResp.data || [];

          // Fetch all hotspots
          const hotspotsPromises = panoramas.map(p => 
            axios.get(`${API}/panoramas/${p.id}/hotspots`).catch(() => ({ data: [] }))
          );
          const hotspotsResults = await Promise.all(hotspotsPromises);
          const allHotspots = hotspotsResults.flatMap(r => r.data || []);

          // Fetch found hotspots
          const foundPromises = panoramas.map(p =>
            axios.get(`${API}/panoramas/${p.id}/hotspots/found?_t=${Date.now()}`, { headers: getAuthHeader() })
              .catch(() => ({ data: { found_hotspot_ids: [] } }))
          );
          const foundResults = await Promise.all(foundPromises);
          const foundHotspotIds = foundResults.flatMap(r => r.data?.found_hotspot_ids || []);

          setGameProgress({
            panoramasTotal: 0,
            panoramasAnswered: 0,
            hotspotsTotal: allHotspots.length,
            hotspotsFound: foundHotspotIds.length
          });
        } else {
          setGameProgress(null);
        }
      } catch (error) {
        console.error('Error fetching game progress:', error);
        setGameProgress(null);
      }
    };
    
    fetchGameProgress();
    
    // Listen for hotspot-found events to update progress in real-time
    const handleHotspotFound = () => {
      console.log("Hotspot found event received, refreshing progress...");
      fetchGameProgress();
    };
    window.addEventListener("hotspot-found", handleHotspotFound);
    
    return () => {
      window.removeEventListener("hotspot-found", handleHotspotFound);
    };
  }, [location.pathname, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [showDropdown]);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    navigate('/');
  };

  // Don't show header on guess/panorama pages
  const isGuessPage = location.pathname.includes('/guess/') || location.pathname.includes('/panoramas/');
  
  if (isGuessPage) {
    return null;
  }
  
  // Show only profile icon on auth/home/game pages
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname.includes('/verify-email/');
  const isHomePage = location.pathname === '/';
  const isGamePage = location.pathname.match(/^\/[a-f0-9-]{36}$/) && !location.pathname.includes('/edit') && !location.pathname.includes('/create');
  const showOnlyProfile = isAuthPage || isHomePage || isGamePage;

  return (
    <div style={{
      position: 'fixed',
      top: '12px',
      right: '0.75rem',
      zIndex: 9999,
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center'
    }}>
      {/* Avatar with Dropdown for authenticated users */}
      {user ? (
        <div ref={dropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          {/* Game Progress Badge - only on game pages */}
          
          {gameProgress && !isGuessPage && gameProgress.hotspotsTotal > 0 && (
            <div 
              style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                color: '#92400e',
                padding: '3px 8px',
                borderRadius: '16px',
                fontSize: '0.75rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                border: '1px solid #f59e0b'
              }}
            >
              {/* Hotspots Progress */}
              {gameProgress.hotspotsTotal > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title="Найдено объектов">
                  <Search size={12} />
                  <span style={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {gameProgress.hotspotsFound}/{gameProgress.hotspotsTotal}
                  </span>
                  <div style={{
                    width: '40px',
                    height: '4px',
                    backgroundColor: gameProgress.hotspotsFound < gameProgress.hotspotsTotal ? '#ef4444' : 'rgba(255,255,255,0.5)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${gameProgress.hotspotsTotal ? (gameProgress.hotspotsFound / gameProgress.hotspotsTotal * 100) : 0}%`,
                      backgroundColor: '#22c55e',
                      borderRadius: '2px'
                    }} />
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Referral registrations bell (left of points) */}
          <ReferralNotificationsBell user={user} />
          
          {/* Points Badge - always visible */}
          <div style={{
            background: userPoints > 0 
              ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
              : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
            color: userPoints > 0 ? '#92400e' : '#6b7280',
            padding: '3px 8px',
            borderRadius: '16px',
            fontSize: '0.75rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            border: userPoints > 0 ? '1px solid #f59e0b' : '1px solid #d1d5db'
          }}>
            ⭐ {Math.round(userPoints)}
          </div>
          <LanguageSwitcher flagOnly style={{ flexShrink: 0 }} />
          <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(!showDropdown);
              }}
              data-testid="profile-btn"
              type="button"
              style={{
                cursor: 'pointer',
                background: 'white',
                borderRadius: '50%',
                padding: '2px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                border: 'none',
                outline: 'none',
                flexShrink: 0
              }}
              title="Меню пользователя"
              aria-label="Меню пользователя"
            >
              <Avatar
                userId={user.id}
                avatarUrl={user.avatar_url}
                fullImageUrl={user.full_image_url}
                userName={user.full_name}
                userEmail={user.email}
                size={36}
                clickable={false}
              />
            </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                top: '4rem',
                right: '1rem',
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                minWidth: '200px',
                maxWidth: 'calc(100vw - 2rem)',
                overflow: 'hidden',
                zIndex: 10000
              }}>
              {/* User Info */}
              <div style={{
                padding: '1rem',
                borderBottom: '1px solid #e5e7eb',
                background: '#f9fafb'
              }}>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                  {user.full_name}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                  {user.email}
                </div>
              </div>

              {/* Menu Items */}
              <div style={{ padding: '0.5rem 0' }}>
                {/* Profile */}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/profile');
                    setShowDropdown(false);
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'background 0.2s',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <User size={18} />
                  <span>Профиль</span>
                </div>

                {/* Virality / Invite friends */}
                <div
                  data-testid="header-menu-virality"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/virality');
                    setShowDropdown(false);
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'background 0.2s',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Share2 size={18} />
                  <span>{t('virality.inviteFriend')}</span>
                </div>

                {/* Admin Panel - only for admins */}
                {hasRole('admin') && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/admin');
                      setShowDropdown(false);
                    }}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'background 0.2s',
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Shield size={18} />
                    <span>Панель управления</span>
                  </div>
                )}

                {/* Create Game - for admin and editor */}
                {hasRole(['admin', 'editor']) && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/create');
                      setShowDropdown(false);
                    }}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'background 0.2s',
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Plus size={18} />
                    <span>Добавить игру</span>
                  </div>
                )}

                {/* Logout */}
                <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogout();
                    }}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      color: '#dc2626',
                      transition: 'background 0.2s',
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <LogOut size={18} />
                    <span>Выйти</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <LanguageSwitcher flagOnly />
          <Button
            variant="outline"
            onClick={() => navigate('/login')}
            data-testid="header-login-btn"
            style={{
              background: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <LogIn size={18} />
            {t("auth.login")}
          </Button>
          <Button
            onClick={() => navigate('/register')}
            data-testid="header-register-btn"
            style={{
              background: '#667eea',
              color: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <UserPlus size={18} />
            {t("auth.register")}
          </Button>
        </>
      )}

      {/* Full Image Modal */}
      {showFullImage && (
        <FullImageModal
          imageUrl={showFullImage}
          onClose={() => setShowFullImage(false)}
        />
      )}
    </div>
  );
};

export default PageHeader;
